import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/utils/class-name";

const badgeVariants = cva(
    "py-0.5 relative inline-flex shrink-0 items-center justify-center gap-1 rounded-md border border-transparent font-medium whitespace-nowrap transition-shadow outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-64 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-3 [button,a&]:cursor-pointer [button,a&]:pointer-coarse:after:absolute [button,a&]:pointer-coarse:after:size-full [button,a&]:pointer-coarse:after:min-h-11 [button,a&]:pointer-coarse:after:min-w-11",
    {
        variants: {
            variant: {
                default: "bg-primary text-primary-foreground [button,a&]:hover:bg-primary/90",
                destructive: "bg-destructive text-white [button,a&]:hover:bg-destructive/90",
                outline:
                    "border-border bg-transparent dark:bg-input/32 [button,a&]:hover:bg-accent/50 dark:[button,a&]:hover:bg-input/48",
                secondary: "bg-secondary text-secondary-foreground [button,a&]:hover:bg-secondary/90",
                info: "bg-info/8 text-info-foreground dark:bg-info/16",
                success: "bg-success/8 text-success-foreground dark:bg-success/16",
                warning: "bg-warning/8 text-warning-foreground dark:bg-warning/16",
                error: "bg-destructive/8 text-destructive dark:bg-destructive/16",
                ghost: "text-foreground",
            },
            size: {
                default: "px-[calc(--spacing(2.5)-1px)] text-base",
                sm: "px-[calc(--spacing(2.5)-1px)] text-[.625rem]",
                md: "px-[calc(--spacing(2.5)-1px)] text-md",
                lg: "px-[calc(--spacing(3)-1px)] text-lg",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    },
);

interface BadgeProps extends useRender.ComponentProps<"span"> {
    variant?: VariantProps<typeof badgeVariants>["variant"];
    size?: VariantProps<typeof badgeVariants>["size"];
}

function Badge({ className, variant, size, render, ...props }: BadgeProps) {
    const defaultProps = {
        "data-slot": "badge",
        className: cn(badgeVariants({ variant, size, className })),
    };

    return useRender({
        defaultTagName: "span",
        render,
        props: mergeProps<"span">(defaultProps, props),
    });
}

export { Badge, badgeVariants };
